import { Link, Navigate } from "react-router-dom";
import { Coins, Gauge, ShieldCheck, Swords, Trophy, Users } from "lucide-react";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { useAuth } from "@/context/AuthContext";

const FEATURES = [
  [Trophy, "Stats & leaderboards", "Kills, playtime and coin leaderboards, live kill and join feeds in Discord."],
  [Coins, "Free in-game economy", "Daily rewards, a shop and kits delivered straight to your inventory. Coins have no real-money value."],
  [Swords, "Battle pass & events", "Level up from playtime, kills and daily challenges for gear rewards."],
  [ShieldCheck, "Protection & teleports", "Offline raid protection, raid alerts, safe zones, TP home and portals."],
  [Users, "Discord community tools", "Tickets, giveaways, reaction roles, clans, bounties and moderation."],
  [Gauge, "Server tools for owners", "Performance graphs, day voting, King of the Day and feature switches."],
];

const Feature = ({ icon: Icon, title, body }) => (
  <div className="rounded-sm border border-[#1E2D4A] bg-[#0B1120]/70 p-5 transition-colors hover:border-[#E5202E]/40">
    <Icon className="h-5 w-5 text-[#E5202E]" aria-hidden />
    <h3 className="mt-3 text-base font-semibold uppercase tracking-wider text-slate-100">{title}</h3>
    <p className="mt-1 text-sm text-slate-400">{body}</p>
  </div>
);

export default function Home() {
  const { session } = useAuth();
  if (session === null) return null;
  if (session) return <Navigate to="/overview" replace />;
  return (
    <div className="flex min-h-screen flex-col bg-[#050811] rg-grid-bg">
      <header className="flex items-center justify-between border-b border-[#1E2D4A] bg-[#070A14] px-4 py-3 sm:px-6">
        <Link to="/" className="flex items-center gap-3"><img src="/ranger-logo.jpg" alt="RANGER" className="h-9 w-9 rounded-sm" /><span className="font-display text-xl font-bold uppercase tracking-[0.25em] text-slate-100">Ranger</span></Link>
        <nav className="flex items-center gap-4 text-xs">
          <Link data-testid="home-nav-browse" to="/browse" className="text-slate-300 hover:text-white">Servers</Link>
          <Link data-testid="home-nav-docs" to="/docs" className="text-slate-300 hover:text-white">Docs</Link>
          <Link data-testid="home-nav-signin" to="/login" className="rounded-sm border border-[#1E2D4A] px-3 py-1.5 text-slate-200 transition-colors hover:border-[#E5202E]">Sign in</Link>
        </nav>
      </header>
      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-16 sm:py-24">
        <div className="rg-label text-[#E5202E]">Rust Console Edition · Xbox / PlayStation</div>
        <h1 data-testid="home-title" className="mt-3 max-w-3xl text-4xl font-bold uppercase leading-[0.95] tracking-wide text-slate-100 sm:text-5xl lg:text-6xl">Community tools for Rust Console servers</h1>
        <p className="mt-5 max-w-2xl text-base text-slate-400">RANGER is a free Discord bot and web dashboard that runs stats, rewards, shops, battle passes and moderation for Rust Console Edition communities.</p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link data-testid="home-browse-cta" to="/browse" className="rounded-sm bg-[#E5202E] px-5 py-3 text-sm font-semibold uppercase tracking-wider text-white transition-colors hover:bg-[#c41a26]">Find a server</Link>
          <Link data-testid="home-signin-cta" to="/login" className="rounded-sm border border-[#1E2D4A] px-5 py-3 text-sm font-semibold uppercase tracking-wider text-slate-200 transition-colors hover:border-[#E5202E]">Player & staff sign in</Link>
        </div>
        <div className="mt-16 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{FEATURES.map(([icon, t, b]) => <Feature key={t} icon={icon} title={t} body={b} />)}</div>
      </main>
      <SiteFooter />
    </div>
  );
}
